// src/services/announcementService.js
// Sprint 4: fully migrated from localStorage to Supabase announcements table
// Fix: author_id FKs point to auth.users, not user_profiles — use user_profiles
// as a separate lookup via author_id match rather than named FK hints.

import { supabase } from '../lib/supabase';
import { getDisplayNames } from './memberDisplayService';

export const announcementService = {
  getAllAnnouncements: async (hoaId) => {
    let query = supabase
      .from('announcements')
      .select(`
        id, title, body, tag, created_at, updated_at, author_id,
        comments:announcement_comments(
          id, body, created_at, author_id
        )
      `)
      .order('created_at', { ascending: false });

    if (hoaId) query = query.eq('hoa_id', hoaId);

    const { data, error } = await query;

    if (error) {
      console.error('announcementService.getAllAnnouncements error:', error.message);
      return [];
    }

    if (!data || data.length === 0) return [];

    // Collect all unique author_ids from announcements and comments
    const authorIds = [
      ...new Set([
        ...data.map(a => a.author_id).filter(Boolean),
        ...data.flatMap(a => (a.comments || []).map(c => c.author_id).filter(Boolean)),
      ])
    ];

    // Names come from get_member_display_names (members cannot read other profiles)
    const profileMap = {};
    const names = await getDisplayNames(authorIds);
    names.forEach((value, id) => { profileMap[id] = value; });

    return data.map(a => ({
      id: a.id,
      title: a.title,
      body: a.body,
      tag: a.tag,
      date: new Date(a.created_at).toLocaleDateString('en-CA'),
      author: profileMap[a.author_id]?.full_name || 'Management',
      comments: (a.comments || []).map(c => ({
        id: c.id,
        author: profileMap[c.author_id]?.full_name || 'Member',
        text: c.body,
        date: new Date(c.created_at).toLocaleString('en-US', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
      })),
    }));
  },

  getAnnouncements: async (hoaId) => announcementService.getAllAnnouncements(hoaId),

  createAnnouncement: async (user, data, hoaId) => {
    if (!hoaId) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('hoa_id')
        .eq('id', user.id)
        .maybeSingle();
      hoaId = profile?.hoa_id;
    }

    if (!hoaId) throw new Error('No HOA associated with user');

    const { error } = await supabase
      .from('announcements')
      .insert({
        hoa_id: hoaId,
        title: data.title,
        body: data.body,
        tag: data.tag || 'General',
        author_id: user.id,
      });

    if (error) throw error;
  },

  addComment: async (announcementId, user, text) => {
    const { error } = await supabase
      .from('announcement_comments')
      .insert({
        announcement_id: announcementId,
        author_id: user.id,
        body: text,
      });

    if (error) throw error;
  },
};
